import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth as defaultUseAuth } from '../../lib/auth';
import type { AuthState } from '../../lib/auth';

interface RequireAdminProps {
  children: ReactNode;
  /** Optional hook override for testing (dependency injection). Defaults to `useAuth`. */
  useAuth?: () => AuthState;
}

/**
 * Route guard — silently redirects non-admin visitors to `/`.
 *
 * §9.2 layer 2 (router gating):
 *   - ready === false  → render nothing (loading state)
 *   - role === 'admin' → render children
 *   - otherwise        → <Navigate to="/" replace /> (no "access denied" leak)
 */
export function RequireAdmin({ children, useAuth = defaultUseAuth }: RequireAdminProps) {
  const { ready, role } = useAuth();
  if (!ready) return null;
  if (role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}
